<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeMount } from "vue";
import { useApiClient } from "@/api/client";
import { useRouter } from "vue-router";
import dayjs from "dayjs";
import { useDownloadExport } from "@/modules/exports/composables/useDownloadExport";
import ExportTypeChip from "@/ui/components/exports/ExportTypeChip.vue";
import UpcomingExportScheduleDO = App.Data.ExportScheduler.UpcomingExportScheduleDO;
import ExportListDO = App.Data.ExportListDO;

const router = useRouter();

const tab = ref<"scheduled" | "exported">("scheduled");

const scrollRef = ref();

watch(tab, async () => {
	await nextTick();
	if (scrollRef.value?.$el) scrollRef.value.$el.scrollTop = 0;
});

const isLoading = ref(true);
const items = ref<UpcomingExportScheduleDO[]>([]);

const isLoadingExecuted = ref(true);
const executedItems = ref<ExportListDO[]>([]);

const { download } = useDownloadExport();

onBeforeMount(() => {
	fetchUpcomingExecutions();
	fetchRecentExecutions();
});

const fetchUpcomingExecutions = async () => {
	isLoading.value = true;
	const response = await useApiClient(
		"exportSchedules"
	).getUpcomingExecutions();
	if (response.isOk()) {
		items.value = response.value;
	}
	isLoading.value = false;
};

const fetchRecentExecutions = async () => {
	isLoadingExecuted.value = true;
	const response = await useApiClient("exports").index({
		page: 1,
		per_page: 100,
		sort: "-created_at",
	});
	if (response.isOk()) {
		executedItems.value = response.value.data;
	}
	isLoadingExecuted.value = false;
};

const formatRelative = (date: string) => dayjs(date).fromNow();

const formatExact = (date: string) => dayjs(date).format("DD MMM YYYY HH:mm");

/**
 * A period bucket label for a date. Near-term dates collapse into
 * "This week" / "Next week"; anything further out is grouped by its month, so
 * a single export months away shows just that month rather than a run of empty
 * week buckets.
 */
const periodLabel = (date: string): string => {
	const target = dayjs(date);
	const days = target.diff(dayjs(), "day");

	if (days >= 0) {
		if (days < 7) return "This week";
		if (days < 14) return "Next week";
		return target.format("MMMM YYYY");
	}

	if (days > -7) return "This week";
	if (days > -14) return "Last week";
	return target.format("MMMM YYYY");
};

type PeriodGroup<T> = { label: string; items: T[] };

/**
 * Groups already date-sorted items into period buckets, keeping first-seen
 * (chronological) order and dropping empty buckets entirely.
 */
const groupByPeriod = <T>(
	list: T[],
	dateOf: (item: T) => string
): PeriodGroup<T>[] => {
	const buckets = new Map<string, T[]>();
	for (const item of list) {
		const label = periodLabel(dateOf(item));
		const bucket = buckets.get(label);
		if (bucket) {
			bucket.push(item);
		} else {
			buckets.set(label, [item]);
		}
	}
	return Array.from(buckets, ([label, groupItems]) => ({
		label,
		items: groupItems,
	}));
};

const scheduledGroups = computed(() =>
	groupByPeriod(items.value, (i) => i.predicted_execution_at)
);

const exportedGroups = computed(() =>
	groupByPeriod(executedItems.value, (i) => i.created_at ?? "")
);

const openSchedule = (item: { export_schedule: { uuid: string } }) => {
	router.push(`/scheduler/${item.export_schedule.uuid}`);
};

const openExport = (item: ExportListDO) => {
	router.push({ name: "exports.view", params: { uuid: item.uuid } });
};
</script>

<template>
	<v-card class="export-activity d-flex flex-column">
		<div class="export-activity__header bg-background">
			<div class="v-card-title">Export Activity</div>
			<v-tabs
				v-model="tab"
				density="default"
				style="background-color: transparent"
			>
				<v-tab value="scheduled"> Upcoming </v-tab>
				<v-tab value="exported"> Exported </v-tab>
			</v-tabs>
		</div>

		<perfect-scrollbar
			ref="scrollRef"
			class="export-activity__scroll"
			:options="{ suppressScrollX: true }"
		>
			<!-- Scheduled -->
			<template v-if="tab === 'scheduled'">
				<div v-if="isLoading" class="export-activity__state">
					<v-progress-circular indeterminate size="22" color="primary" />
				</div>
				<div v-else-if="!scheduledGroups.length" class="export-activity__state">
					No upcoming exports are scheduled.
				</div>
				<template v-else>
					<div
						v-for="group in scheduledGroups"
						:key="group.label"
						class="ea-group"
					>
						<div class="ea-group__head">
							<span class="ea-group__label">{{ group.label }}</span>
							<span class="ea-group__count">{{ group.items.length }}</span>
							<span class="ea-group__line" />
						</div>

						<div
							v-for="item in group.items"
							:key="item.export_schedule.uuid + item.predicted_execution_at"
							class="ea-row"
							@click="openSchedule(item)"
						>
							<ExportTypeChip
								class="ea-row__chip"
								:export-type="item.export_schedule.export_type.name_clean"
								size="small"
								density="comfortable"
							/>
							<div class="ea-row__main">
								<div class="ea-row__name">
									{{ item.export_schedule.title }}
								</div>
							</div>
							<span
								class="ea-row__due"
								v-tooltip="
									`Scheduled at: ${formatExact(item.predicted_execution_at)}`
								"
							>
								<v-icon size="12">fa: fa-light fa-clock</v-icon>
								{{ formatRelative(item.predicted_execution_at) }}
							</span>
						</div>
					</div>
					<div v-if="items.length === 100" class="ea-more">
						<v-icon size="11">fa: fa-light fa-ellipsis</v-icon>
						<span>More upcoming exports</span>
					</div>
				</template>
			</template>

			<!-- Exported -->
			<template v-else>
				<div v-if="isLoadingExecuted" class="export-activity__state">
					<v-progress-circular indeterminate size="22" color="primary" />
				</div>
				<div v-else-if="!exportedGroups.length" class="export-activity__state">
					No exports have run yet.
				</div>
				<template v-else>
					<div
						v-for="group in exportedGroups"
						:key="group.label"
						class="ea-group"
					>
						<div class="ea-group__head">
							<span class="ea-group__label">{{ group.label }}</span>
							<span class="ea-group__count">{{ group.items.length }}</span>
							<span class="ea-group__line" />
						</div>

						<div
							v-for="item in group.items"
							:key="item.uuid"
							class="ea-row"
							@click="openExport(item)"
						>
							<ExportTypeChip
								class="ea-row__chip"
								:export-type="item.export_type_name"
								size="small"
								density="comfortable"
							/>
							<div class="ea-row__main">
								<div class="ea-row__name">
									{{ item.file_name }}
								</div>
							</div>
							<span
								class="ea-row__due"
								v-tooltip="`Exported at: ${formatExact(item.created_at ?? '')}`"
							>
								<v-icon size="12">fa: fa-light fa-clock</v-icon>
								{{ formatRelative(item.created_at ?? "") }}
							</span>
							<v-btn
								class="ea-row__download"
								variant="text"
								size="small"
								density="compact"
								:disabled="item.status !== 'success'"
								v-tooltip="
									item.status === 'success'
										? `Download ${item.file_name}`
										: 'No file available'
								"
								@click.stop="download(item.uuid)"
							>
								<v-icon size="14">fa: fa-light fa-file-arrow-down</v-icon>
							</v-btn>
						</div>
					</div>
					<div v-if="executedItems.length === 100" class="ea-more">
						<v-icon size="11">fa: fa-light fa-ellipsis</v-icon>
						<span>More past exports</span>
					</div>
				</template>
			</template>
		</perfect-scrollbar>

		<v-spacer />

		<div class="d-flex pa-2">
			<v-spacer />
			<v-chip variant="flat" to="/scheduler"> Go to scheduler </v-chip>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.export-activity {
	height: 100%;

	&__header {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		height: 48px;
		flex: 0 0 auto;
	}

	&__scroll {
		flex: 1 1 auto;
		min-height: 0;
		padding: 4px 8px 8px;
	}

	&__state {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 120px;
		color: rgba(var(--v-theme-on-surface), 0.6);
		font-size: 0.875rem;
	}
}

.ea-group {
	&__head {
		display: flex;
		align-items: center;
		gap: 9px;
		padding: 10px 8px 4px;
	}

	&__label {
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.07em;
		text-transform: uppercase;
		color: rgba(var(--v-theme-on-surface), 0.55);
	}

	&__count {
		font-size: 11px;
		font-weight: 700;
		color: rgba(var(--v-theme-on-surface), 0.45);
		background: rgba(var(--v-theme-on-surface), 0.06);
		border-radius: 999px;
		padding: 1px 7px;
	}

	&__line {
		flex: 1;
		height: 1px;
		background: rgba(var(--v-theme-on-surface), 0.08);
	}
}

.ea-more {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	margin-top: 6px;
	padding: 8px;
	font-size: 0.7rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: rgba(var(--v-theme-on-surface), 0.4);
	user-select: none;
}

.ea-row {
	display: flex;
	align-items: center;
	gap: 10px;
	min-height: 34px;
	padding: 8px;
	border-radius: 8px;
	cursor: pointer;
	transition: background 0.12s ease;

	&:hover {
		background: rgba(var(--v-theme-on-surface), 0.04);
	}

	&__chip {
		flex: 0 0 auto;
		display: inline-flex;
		align-items: center;
		height: 34px;

		:deep(.v-chip__content) {
			font-size: 0.75rem;
		}
	}

	&__main {
		flex: 1 1 auto;
		min-width: 0;
	}

	&__name {
		font-size: 0.875rem;
		font-weight: 600;
		line-height: 1.25;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__due {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 0.8rem;
		color: rgba(var(--v-theme-on-surface), 0.55);
		white-space: nowrap;
		flex: 0 0 auto;
	}

	&__download {
		flex: 0 0 auto;
		width: 34px;
		height: 34px;
	}
}
</style>
